import { DealStatusMultiSelect, ProductSelect } from '@repo/crm-atoms';
import { message } from '@repo/ui';
import { translate, TranslationEntity } from '@repo/utils';
import { type NodeProps } from '@xyflow/react';
import {
  Button, Card, Col, Divider, Flex, Row, Typography,
} from 'antd';
import NodeMenu from 'automations/components/NodeMenu';
import { useAutomationDispatch } from 'automations/hooks/useAutomationDispatch';
import useAutomationId from 'automations/hooks/useAutomationId';
import useCommonNodeHandles from 'automations/hooks/useCommonNodeHandles';
import { addAdditionalOptions } from 'automations/store/reactFlowSlice';
import { type DealCreateNodeType, nodeSchemas } from 'automations/types/nodeTypes';
import { saveTriggerNode } from 'automations/utils/triggerNode';
import { useCallback, useState } from 'react';
import { ZodError } from 'zod';

import styles from './DealCreateMenu.module.scss';

export default function DealCreateMenu({
  node,
}: {
  node: NodeProps<DealCreateNodeType>;
}) {
  const automationId = useAutomationId();
  const dispatch = useAutomationDispatch();
  const { setMenuOpen, contentMode } = useCommonNodeHandles(node);
  const [isLoading, setIsLoading] = useState(false);

  const [productUuids, setProductUuids] = useState<string[]>(
    node.data.additionalOptions?.createDeal?.product?.criterias?.map((c) => c.value) ?? [],
  );
  const [statusUuids, setStatusUuids] = useState<string[]>(
    node.data.additionalOptions?.createDeal?.dealStatus?.criterias?.map((c) => c.value) ?? [],
  );

  const handleSave = useCallback(async () => {
    setIsLoading(true);
    try {
      const createDeal: {
        product?: { comparator: 'or'; criterias: { value: string }[] };
        dealStatus?: { comparator: 'or'; criterias: { value: string }[] };
      } = {};
      if (productUuids.length) {
        createDeal.product = { comparator: 'or', criterias: productUuids.map((value) => ({ value })) };
      }
      if (statusUuids.length) {
        createDeal.dealStatus = { comparator: 'or', criterias: statusUuids.map((value) => ({ value })) };
      }

      const parsedNodeData = nodeSchemas.DealCreateNodeData.parse({
        ...node.data,
        additionalOptions: Object.keys(createDeal).length ? { createDeal } : {},
      });

      dispatch(addAdditionalOptions({
        nodeId: node.id,
        additionalOptions: parsedNodeData.additionalOptions ?? {},
      }));

      await dispatch(saveTriggerNode(node.id, automationId, contentMode));
      message.success(translate(`#{${TranslationEntity.automation}} updated`, [{ capitalize: true }]));
      setMenuOpen(false);
    } catch (error: any) {
      const errorMessage = error instanceof ZodError
        ? error.issues.map((issue) => issue.message).join(', ')
        : error?.data?.message ?? translate(`Failed to update #{${TranslationEntity.automation}}`);
      message.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [dispatch, node.data, node.id, automationId, contentMode, setMenuOpen, productUuids, statusUuids]);

  return (
    <NodeMenu
      node={node}
      drawerProps={{
        title: translate(
          `#{${TranslationEntity.automation}} Trigger - When a Deal is created`,
          [{ capitalize: true }],
        ),
      }}
    >
      <Card title={contentMode === 'edit' ? 'Edit Trigger' : 'Set Trigger'} className={styles.containerCard}>
        <p>
          This automation triggers when a deal is created. Optionally scope it to specific
          products or creation statuses (leave empty to trigger on every new deal).
        </p>
        <Row align="middle" gutter={[0, 8]}>
          <Col span={6}><Typography.Text strong>Product is any of</Typography.Text></Col>
          <Col span={18}>
            <ProductSelect
              value={productUuids}
              onChange={setProductUuids}
              style={{ width: '100%' }}
              showSearch
              optionFilterProp="label"
            />
          </Col>
        </Row>
        <Divider dashed />
        <Row align="middle" gutter={[0, 8]}>
          <Col span={6}><Typography.Text strong>Deal status is any of</Typography.Text></Col>
          <Col span={18}>
            <DealStatusMultiSelect
              value={statusUuids}
              onChange={setStatusUuids}
              style={{ width: '100%' }}
              showSearch
              optionFilterProp="label"
            />
          </Col>
        </Row>
      </Card>

      <Flex justify="flex-end" gap={16} className={styles.actionButton}>
        <Button onClick={() => setMenuOpen(false)}>Cancel</Button>
        <Button type="primary" onClick={handleSave} loading={isLoading} disabled={isLoading}>
          Set Trigger
        </Button>
      </Flex>
    </NodeMenu>
  );
}
